/**
 * WhatsApp inbox wire shapes: the FROZEN v2.1 contract (`Conversation`,
 * `Message`, `ConfirmationBatch`) and their defensive mappers.
 *
 * Shared by both inbox adapters (PROD-1849):
 *   - the demo adapter (./demo-data-adapter.ts, the bundled seed), and
 *   - the real Notifications API adapter (./services/inbox-api.ts,
 *     `/api/Notifications/whatsapp/inbox/...`, contract of 23 Oct 2026).
 *
 * Do not add fields the contract does not carry. The backend contract is
 * "the v2.1 types, no new fields; later changes only ADD fields". The mappers
 * degrade unknown enum values to safe defaults and ignore extra fields, so an
 * additive server change never breaks the inbox.
 */

// ---------------------------------------------------------------------------
// Wire contract v2.1 shapes
// ---------------------------------------------------------------------------

export type ConversationStatus =
  | 'ai-handling'
  | 'needs-human'
  | 'human-owned'
  | 'closed';

export interface Conversation {
  id: string;
  orgId: string;
  /** Null for provisional conversations (inbound from an unlinked number). */
  patientId: string | null;
  patientDisplay: string;
  waPhoneMasked: string;
  status: ConversationStatus;
  assignedStaffId?: string | null;
  assignedStaffDisplay?: string | null;
  lastMessageAt: string;
  lastMessagePreview: string;
  unreadCount: number;
  activeAppointmentIds: string[];
}

export type MessageDirection = 'inbound' | 'outbound';
export type MessageProvenance = 'ai' | 'human-staff' | 'patient' | 'system';
export type MessageIntent = 'confirm' | 'reschedule' | 'cancel';
/** `unknown` (PROD-3341, additive): a template send whose request to Meta threw; the patient may have it. */
export type DeliveryStatus = 'queued' | 'sent' | 'delivered' | 'read' | 'failed' | 'unknown';

export interface Message {
  id: string;
  externalMessageId: string | null;
  conversationId: string;
  appointmentId?: string | null;
  direction: MessageDirection;
  sentAt: string;
  body: string;
  templateName?: string | null;
  /** Clinic-side NLU interpretation — null until PROD-1316 classifies it. */
  intent?: MessageIntent | null;
  confidence?: number | null;
  language?: string | null;
  provenance: MessageProvenance;
  deliveryStatus: DeliveryStatus;
  failureReason?: string | null;
  /** PROD-3335 (additive): the patient's file or photo. Null when the message has none. */
  attachment?: MessageAttachment | null;
}

/** PROD-3340: 'audio' is a voice message (played in the bubble, never sent to intake). */
export type AttachmentKind = 'image' | 'document' | 'audio';
export type AttachmentStatus = 'pending' | 'stored' | 'unsupported' | 'too_large' | 'failed';

/** The attachment's intake record (`PUT .../attachment/intake`). Null until it was sent to intake. */
export interface AttachmentIntake {
  status: string;
  batchId: string | null;
}

/**
 * A WhatsApp file or photo (PROD-3335, additive to v2.1). The caption is also the message `body`,
 * so it is shown once, as the bubble text. The server decides `too_large` (over 10 MB).
 */
export interface MessageAttachment {
  id: string;
  kind: AttachmentKind;
  mimeType: string;
  fileName: string | null;
  sizeBytes: number | null;
  caption: string | null;
  status: AttachmentStatus;
  intake: AttachmentIntake | null;
}

export interface ConfirmationBatch {
  batchId: string;
  orgId: string;
  scheduledDate: string;
  templateName: string;
  total: number;
  awaiting: number;
  confirmed: number;
  rescheduled: number;
  needsCall: number;
}


// ---------------------------------------------------------------------------
// Defensive mappers
// ---------------------------------------------------------------------------

export type Raw = Record<string, unknown>;

export function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

export function strOrNull(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

export function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

const CONVERSATION_STATUSES: readonly ConversationStatus[] = [
  'ai-handling',
  'needs-human',
  'human-owned',
  'closed',
];

const DELIVERY_STATUSES: readonly DeliveryStatus[] = [
  'queued',
  'sent',
  'delivered',
  'read',
  'failed',
  'unknown',
];

const INTENTS: readonly MessageIntent[] = ['confirm', 'reschedule', 'cancel'];

const PROVENANCES: readonly MessageProvenance[] = [
  'ai',
  'human-staff',
  'patient',
  'system',
];

function oneOf<T extends string>(
  v: unknown,
  allowed: readonly T[],
  fallback: T,
): T {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v)
    ? (v as T)
    : fallback;
}

export function mapConversation(raw: Raw): Conversation {
  return {
    id: str(raw.id),
    orgId: str(raw.orgId),
    patientId: strOrNull(raw.patientId),
    patientDisplay: str(raw.patientDisplay, 'Unknown'),
    waPhoneMasked: str(raw.waPhoneMasked),
    // Unknown status degrades to needs-human — the safe triage bucket: a
    // human looks rather than the AI silently owning an unclassified state.
    status: oneOf(raw.status, CONVERSATION_STATUSES, 'needs-human'),
    assignedStaffId: strOrNull(raw.assignedStaffId),
    assignedStaffDisplay: strOrNull(raw.assignedStaffDisplay),
    lastMessageAt: str(raw.lastMessageAt),
    lastMessagePreview: str(raw.lastMessagePreview),
    unreadCount: num(raw.unreadCount),
    activeAppointmentIds: Array.isArray(raw.activeAppointmentIds)
      ? raw.activeAppointmentIds.filter((x): x is string => typeof x === 'string')
      : [],
  };
}

export function mapMessage(raw: Raw): Message {
  return {
    id: str(raw.id),
    externalMessageId: strOrNull(raw.externalMessageId),
    conversationId: str(raw.conversationId),
    appointmentId: strOrNull(raw.appointmentId),
    // Unknown direction renders LEFT (inbound) — misplacing a patient
    // message on the clinic side is the worse failure.
    direction: oneOf(raw.direction, ['inbound', 'outbound'] as const, 'inbound'),
    sentAt: str(raw.sentAt),
    body: str(raw.body),
    templateName: strOrNull(raw.templateName),
    intent:
      typeof raw.intent === 'string' &&
      (INTENTS as readonly string[]).includes(raw.intent)
        ? (raw.intent as MessageIntent)
        : null,
    confidence: typeof raw.confidence === 'number' ? raw.confidence : null,
    language: strOrNull(raw.language),
    // Unknown provenance degrades to 'system' — never falsely attribute
    // content to the AI (would draw the provenance stripe) or to a human.
    provenance: oneOf(raw.provenance, PROVENANCES, 'system'),
    deliveryStatus: oneOf(raw.deliveryStatus, DELIVERY_STATUSES, 'queued'),
    failureReason: strOrNull(raw.failureReason),
    attachment: mapAttachment(raw.attachment),
  };
}

const ATTACHMENT_STATUSES: readonly AttachmentStatus[] = ['pending', 'stored', 'unsupported', 'too_large', 'failed'];

/** The attachment, or null when absent. Unknown kind reads as a document; unknown status as failed. */
export function mapAttachment(v: unknown): MessageAttachment | null {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return null;
  const raw = v as Raw;
  const intake = typeof raw.intake === 'object' && raw.intake !== null ? (raw.intake as Raw) : null;
  return {
    id: str(raw.id),
    // An unknown kind gets the file chip, never an <img> of bytes that may not be an image.
    kind: oneOf(raw.kind, ['image', 'document', 'audio'] as const, 'document'),
    mimeType: str(raw.mimeType, 'application/octet-stream'),
    fileName: strOrNull(raw.fileName),
    sizeBytes: typeof raw.sizeBytes === 'number' && Number.isFinite(raw.sizeBytes) ? raw.sizeBytes : null,
    caption: strOrNull(raw.caption),
    // An unknown status offers no action on the file: it reads as failed.
    status: oneOf(raw.status, ATTACHMENT_STATUSES, 'failed'),
    intake: intake ? { status: str(intake.status, 'sent'), batchId: strOrNull(intake.batchId) } : null,
  };
}

// ---------------------------------------------------------------------------
// Presentation helpers shared by the inbox components
// ---------------------------------------------------------------------------

/** Inbox ordering: needs-human first, then lastMessageAt descending. */
export function sortConversations(convs: Conversation[]): Conversation[] {
  return [...convs].sort((a, b) => {
    const aNeeds = a.status === 'needs-human' ? 0 : 1;
    const bNeeds = b.status === 'needs-human' ? 0 : 1;
    if (aNeeds !== bNeeds) return aNeeds - bNeeds;
    // ISO-8601 timestamps compare correctly as strings.
    return b.lastMessageAt.localeCompare(a.lastMessageAt);
  });
}
